
import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import { Separator } from "@/components/ui/separator";

export function FeaturesSettings() {
  const { toast } = useToast();
  const { getBooleanSetting, updateBooleanSetting, clearCache, isLoading } = useSystemSettings();
  const [priceComparisonEnabled, setPriceComparisonEnabled] = useState(true);
  const [currencyConversionEnabled, setCurrencyConversionEnabled] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const settingsLoaded = useRef(false);

  // Load current settings
  useEffect(() => {
    const loadSettings = async () => {
      if (settingsLoaded.current) return;
      
      try {
        // Load price comparison setting
        console.log("FeaturesSettings: Loading price comparison setting");
        const comparisonSetting = await getBooleanSetting("enable_price_comparison", true);
        console.log("FeaturesSettings: Price comparison setting loaded:", comparisonSetting);
        setPriceComparisonEnabled(comparisonSetting);
        
        // Load currency conversion setting
        console.log("FeaturesSettings: Loading currency conversion setting");
        const conversionSetting = await getBooleanSetting("enable_currency_conversion", true);
        console.log("FeaturesSettings: Currency conversion setting loaded:", conversionSetting);
        setCurrencyConversionEnabled(conversionSetting);
        
        settingsLoaded.current = true;
      } catch (error) {
        console.error("Error loading feature settings:", error);
        toast({
          title: "Error",
          description: "Failed to load feature settings.",
          variant: "destructive",
        });
      } finally {
        setIsInitializing(false);
      }
    };

    loadSettings();
  }, [getBooleanSetting, toast]);

  const handlePriceComparisonToggle = async (enabled: boolean) => {
    try {
      console.log(`FeaturesSettings: Toggling price comparison to ${enabled}`);
      
      // Update UI state immediately for responsive feel
      setPriceComparisonEnabled(enabled);
      
      // Update the setting in the database
      const success = await updateBooleanSetting("enable_price_comparison", enabled);
      
      if (success) {
        console.log(`FeaturesSettings: Price comparison successfully set to ${enabled}`);
        
        // Clear settings cache to ensure all components get the new setting
        clearCache();
        
        toast({
          title: "Setting updated",
          description: `Price comparison feature ${enabled ? "enabled" : "disabled"}.`,
        });
      } else {
        // Revert UI state if update failed
        console.error(`FeaturesSettings: Failed to set price comparison to ${enabled}`);
        setPriceComparisonEnabled(!enabled);
        toast({
          title: "Error",
          description: "Failed to update price comparison setting.",
          variant: "destructive",
        });
      }
    } catch (error) {
      // Revert UI state if update failed
      setPriceComparisonEnabled(!enabled);
      console.error("Error updating price comparison setting:", error);
      toast({
        title: "Error",
        description: "Failed to update price comparison setting.",
        variant: "destructive",
      });
    }
  };
  
  const handleCurrencyConversionToggle = async (enabled: boolean) => {
    try {
      console.log(`FeaturesSettings: Toggling currency conversion to ${enabled}`);
      
      // Update UI state immediately for responsive feel
      setCurrencyConversionEnabled(enabled);
      
      // Update the setting in the database
      const success = await updateBooleanSetting("enable_currency_conversion", enabled);
      
      if (success) {
        console.log(`FeaturesSettings: Currency conversion successfully set to ${enabled}`);
        
        // Clear settings cache to ensure all components get the new setting
        clearCache();
        
        toast({
          title: "Setting updated",
          description: `Currency conversion feature ${enabled ? "enabled" : "disabled"}.`,
        });
      } else {
        // Revert UI state if update failed
        console.error(`FeaturesSettings: Failed to set currency conversion to ${enabled}`);
        setCurrencyConversionEnabled(!enabled);
        toast({
          title: "Error",
          description: "Failed to update currency conversion setting.",
          variant: "destructive",
        });
      }
    } catch (error) {
      // Revert UI state if update failed
      setCurrencyConversionEnabled(!enabled);
      console.error("Error updating currency conversion setting:", error);
      toast({
        title: "Error",
        description: "Failed to update currency conversion setting.",
        variant: "destructive",
      });
    }
  };

  if (isInitializing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Feature Settings</CardTitle>
          <CardDescription>Loading settings...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Feature Settings</CardTitle>
        <CardDescription>
          Enable or disable application features
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Contract Features</h3>
          <Separator className="my-2" />
          
          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-0.5">
              <Label htmlFor="price-comparison">Price Comparison</Label>
              <p className="text-sm text-muted-foreground">
                Show price comparison indicators on contract costs based on historical data
              </p>
            </div>
            <Switch
              id="price-comparison"
              checked={priceComparisonEnabled}
              onCheckedChange={handlePriceComparisonToggle}
              disabled={isLoading}
            />
          </div>
          
          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-0.5">
              <Label htmlFor="currency-conversion">Currency Conversion</Label>
              <p className="text-sm text-muted-foreground">
                Enable currency conversion to display contract costs in user's default currency
              </p>
            </div>
            <Switch
              id="currency-conversion"
              checked={currencyConversionEnabled}
              onCheckedChange={handleCurrencyConversionToggle}
              disabled={isLoading}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
